import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  ThemingProps,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export class ConfirmationNotCompleted extends Error {}

export interface ConfirmationModalConfig {
  title: string;
  description: React.ReactNode;
  onConfirmed: () => Promise<void> | void;
  onCancelled?: () => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  size?: ThemingProps<"Modal">["size"];
}

export const useConfirmationModal = (): {
  show: (config: ConfirmationModalConfig) => void;
  modal: React.ReactElement;
} => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ConfirmationModalConfig>();
  const [isLoading, setIsLoading] = useState(false);

  // Both ways out clear the spinner. Escape and the backdrop reach us through Chakra's onClose while
  // a confirmation may still be in flight, and the hook outlives the modal — one stranded flag would
  // otherwise arrive on the next confirmation the same component raises, disabled and spinning.
  const cancel = () => {
    config?.onCancelled?.();
    setConfig(undefined);
    setIsLoading(false);
  };

  return {
    show: (config: ConfirmationModalConfig) => setConfig(config),
    modal: (
      <>
        {config && (
          <Modal isOpen={true} onClose={cancel} size={config.size ?? "3xl"}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{config.title}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {typeof config.description === "string" ? <Text>{config.description}</Text> : config.description}
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={cancel} isDisabled={isLoading}>
                  {config.cancelButtonText ?? t("Cancel")}
                </Button>
                <Button
                  variant="outline"
                  colorScheme={config.confirmButtonColor ?? "teal"}
                  onClick={async () => {
                    const shown = config;
                    setIsLoading(true);
                    try {
                      await shown.onConfirmed();
                      // Only ever puts away the modal this click belongs to. Dismissed mid-flight,
                      // the buyer may already have raised another one, and it is not this one's to
                      // close.
                      setConfig((current) => (current === shown ? undefined : current));
                    } catch (e) {
                      if (!(e instanceof ConfirmationNotCompleted)) throw e;
                    } finally {
                      // A throwing onConfirmed leaves the modal standing so the buyer can try again
                      // or back out — but never spinning at them.
                      setIsLoading(false);
                    }
                  }}
                  isLoading={isLoading}
                >
                  {config.confirmButtonText ?? t("Confirm")}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </>
    ),
  };
};
